/**
 * S-61 漁場情報 登録・編集（F-CAT-01）。担当: G4
 * kintone 版 `apps/漁場情報登録/*`：ヘッダ（漁船・漁法・漁具・調査航海期間・報告者・水揚げ日・
 * 調査操業漁場・水揚合計額/出荷控除額/手取金額）、参照情報（CSV 取込データ）、漁場情報明細（行コピー）。
 * R08.10.08 検証指摘対応：報告書提出日は不要のため画面から外す（列・API は互換のため残す）、漁法は表１の一覧、
 * 「表面水温（℃）」表記、標準和名⇔地方名の連動、船主・船長（速報）の表示、手取金額・調査操業漁場の自動設定（API）。
 */
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm, useWatch, type Control, type FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Database, Save, Trash2 } from 'lucide-react';
import {
  Button,
  DataTable,
  DateField,
  EmptyState,
  ErrorState,
  FormActions,
  FormField,
  FormGrid,
  Loading,
  NumberField,
  PageHeader,
  Panel,
  Select,
  SubTableEditor,
  Tabs,
  TextField,
  useConfirm,
  useToast,
  type Column,
} from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { BusinessOperatorSelect, useCodeValues, VesselSelect } from '@/features/masters';
import { errorMessage } from '@/lib/api';
import { applyApiErrors } from '@/lib/form';
import { formatNumber } from '@/lib/format';
import { useCaptains, useDeleteFishingGround, useFishingGround, useFishSpecies, useReference, useSaveFishingGround, type FishingGroundDetail, type ReferenceItem } from './api';
import { emptyEntry, emptyForm, findSpeciesByName, fishingGroundFormSchema, toForm, toInput, type FishingGroundFormValues } from './schema';
import './catch.css';

export function FishingGroundFormPage() {
  const { id } = useParams();
  const recordId = id ? Number(id) : null;
  const { data, isPending, error, refetch } = useFishingGround(recordId);
  if (recordId != null) {
    if (isPending) return <Loading block />;
    if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  }
  return <FishingGroundForm key={recordId ?? 'new'} record={recordId != null ? data : undefined} />;
}

/** 数値セル（明細用） */
function NumCell({ control, name, label, decimals, width = 'full' }: { control: Control<FishingGroundFormValues>; name: FieldPath<FishingGroundFormValues>; label: string; decimals?: number; width?: 'xs' | 'sm' | 'full' }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <NumberField
          aria-label={label}
          value={(field.value as number | null) ?? null}
          onChange={field.onChange}
          onBlur={field.onBlur}
          decimals={decimals}
          width={width}
          invalid={!!fieldState.error}
          title={fieldState.error?.message}
        />
      )}
    />
  );
}

function FishingGroundForm({ record }: { record?: FishingGroundDetail }) {
  const isNew = !record;
  const me = useCurrentUser();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { year, yearPath } = useFiscalYear();
  const codes = useCodeValues();
  const species = useFishSpecies();
  const save = useSaveFishingGround(record?.id ?? null);
  const remove = useDeleteFishingGround();
  const ownOperator = me.organizationType === '漁協' && me.businessOperatorId != null ? Number(me.businessOperatorId) : null;

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FishingGroundFormValues>({
    resolver: zodResolver(fishingGroundFormSchema),
    defaultValues: record ? toForm(record) : emptyForm(year, ownOperator),
  });
  const [operatorId, voyageFrom, voyageTo, entries, vesselId, ownerName, fishingMethod] = useWatch({
    control,
    name: ['businessOperatorId', 'voyageFrom', 'voyageTo', 'entries', 'vesselId', 'ownerName', 'fishingMethod'],
  });
  const reference = useReference(year, operatorId, voyageFrom, voyageTo);
  const captains = useCaptains(vesselId, voyageFrom, voyageTo);
  const captainNames = [...new Set((captains.data?.items ?? []).map((c) => c.captainName).filter(Boolean))].join('・');
  // 漁法は表１の一覧。一覧に無い保存済みの値（旧区分）は選択肢の末尾に残して表示する
  const methodOptions = useMemo(() => {
    const list = (codes.data?.catchFishingMethods ?? []).map((m) => ({ value: m.value, label: m.label }));
    if (fishingMethod && !list.some((m) => m.value === fishingMethod)) list.push({ value: fishingMethod, label: `${fishingMethod}（旧区分）` });
    return list;
  }, [codes.data, fishingMethod]);
  /** 標準和名・地方名の入力後、魚種マスタで 1 件に決まれば魚種と相手側の名称を補完する（相手側が空欄のときのみ） */
  const linkSpecies = (path: `entries.${number}`, field: 'standardName' | 'localName') => {
    const sp = findSpeciesByName(species.data?.species ?? [], field, getValues(`${path}.${field}`));
    if (!sp) return;
    const other = field === 'standardName' ? 'localName' : 'standardName';
    if (!getValues(`${path}.${other}`)) setValue(`${path}.${other}`, sp[other] ?? null);
    if (getValues(`${path}.fishSpeciesId`) == null) setValue(`${path}.fishSpeciesId`, sp.id);
  };
  const speciesOptions = useMemo(
    () => (species.data?.species ?? []).map((s) => ({ value: s.id, label: s.localName ? `${s.standardName}（${s.localName}）` : s.standardName })),
    [species.data],
  );
  const listPath = yearPath('fishing-grounds');

  const onSubmit = handleSubmit(async (values) => {
    try {
      const saved = await save.mutateAsync(toInput(values));
      toast.success(isNew ? '漁場情報を登録しました' : '漁場情報を更新しました');
      for (const w of saved.warnings ?? []) toast.warning('確認してください', `${w.rowIndex != null ? `明細 ${w.rowIndex + 1} 行目：` : ''}${w.message}`);
      navigate(yearPath(`fishing-grounds/${saved.id}`));
    } catch (e) {
      if (!applyApiErrors(e, setError)) toast.error('保存できませんでした', errorMessage(e));
    }
  });

  const onDelete = async () => {
    if (!record) return;
    if (!(await confirm({ title: '漁場情報を削除しますか？', message: 'この操作は取り消せません。', tone: 'danger', confirmLabel: '削除する' }))) return;
    try {
      await remove.mutateAsync(record.id);
      toast.success('漁場情報を削除しました');
      navigate(listPath);
    } catch (e) {
      toast.error('削除できませんでした', errorMessage(e));
    }
  };

  const landingTotal = entries.reduce((s, e) => s + (e.weight ?? 0), 0);

  const refColumns: Column<ReferenceItem>[] = [
    { key: 'ownerName', header: '船主名', sortable: true, width: 130 },
    { key: 'departureDate', header: '出港日', width: 104, render: (r) => <span className="bs-num">{r.departureDate ?? ''}</span> },
    { key: 'arrivalDate', header: '入港日', width: 104, render: (r) => <span className="bs-num">{r.arrivalDate ?? ''}</span> },
    { key: 'landingDate', header: '水揚げ日', width: 104, render: (r) => <span className="bs-num">{r.landingDate}</span> },
    { key: 'fishingMethodName', header: '漁法', width: 100 },
    { key: 'speciesTypeName', header: '類型', width: 100 },
    { key: 'speciesName', header: '魚種' },
    { key: 'count', header: '数量', numeric: true, width: 80 },
    { key: 'unitPrice', header: '単価', numeric: true, width: 90 },
    { key: 'amount', header: '金額', numeric: true, width: 110 },
  ];

  return (
    <form onSubmit={onSubmit} noValidate>
      <PageHeader
        eyebrow="漁獲調査"
        title={isNew ? '漁場情報 新規登録' : '漁場情報 編集'}
        description="調査航海（出港〜帰港）ごとに漁場調査日・漁獲位置・魚種を登録します。緯度・経度から農林漁区番号を自動算出します。"
      />
      <Panel title="基本情報">
        <FormGrid columns={3}>
          <FormField label="事業実施者" required error={errors.businessOperatorId?.message}>
            <Controller
              control={control}
              name="businessOperatorId"
              render={({ field }) => (
                <BusinessOperatorSelect
                  fiscalYear={year}
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(v);
                    if (v !== field.value) setValue('vesselId', null);
                  }}
                  placeholder="選択してください"
                  width="full"
                  disabled={!isNew}
                />
              )}
            />
          </FormField>
          <FormField label="漁船（船名・漁船登録番号）" required error={errors.vesselId?.message} span={2}>
            <Controller
              control={control}
              name="vesselId"
              render={({ field }) => (
                <VesselSelect
                  fiscalYear={year}
                  businessOperatorId={operatorId}
                  value={field.value}
                  width="full"
                  fallbackLabel={record ? `${record.vesselName}（${record.registrationNo}）` : undefined}
                  onChange={(vid, opt) => {
                    field.onChange(vid);
                    if (opt) {
                      if (!getValues('ownerName')) setValue('ownerName', opt.ownerName);
                      // 参加船の漁業種類のうち、漁獲調査の漁法一覧（表１）にあるものだけを初期値にする
                      const catchNames = (codes.data?.catchFishingMethods ?? []).map((m) => m.value);
                      const method = opt.fishingMethods.find((m) => catchNames.includes(m));
                      if (!getValues('fishingMethod') && method) setValue('fishingMethod', method);
                      if (getValues('businessOperatorId') == null) setValue('businessOperatorId', opt.businessOperatorId);
                    }
                  }}
                />
              )}
            />
          </FormField>
          <FormField label="船主名" error={errors.ownerName?.message}>
            <TextField {...register('ownerName')} />
          </FormField>
          <FormField label="漁法" error={errors.fishingMethod?.message}>
            <Controller
              control={control}
              name="fishingMethod"
              render={({ field }) => (
                <Select
                  placeholder="選択してください"
                  options={methodOptions}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  onBlur={field.onBlur}
                />
              )}
            />
          </FormField>
          <FormField label="漁具・数量" error={errors.gear?.message ?? errors.gearCount?.message}>
            <div className="bs-catch-inline">
              <TextField {...register('gear')} aria-label="漁具" placeholder="漁具" />
              <NumCell control={control} name="gearCount" label="漁具数量" width="sm" />
            </div>
          </FormField>
          <FormField label="調査航海期間" required error={errors.voyageFrom?.message ?? errors.voyageTo?.message} span={2}>
            <div className="bs-catch-period">
              <DateField aria-label="調査航海期間（開始）" showWareki {...register('voyageFrom')} />
              <span>〜</span>
              <DateField aria-label="調査航海期間（終了）" showWareki {...register('voyageTo')} />
            </div>
          </FormField>
          <FormField label="報告者氏名" error={errors.reporterName?.message}>
            <TextField {...register('reporterName')} />
          </FormField>
        </FormGrid>
      </Panel>

      <Panel
        title="水揚げ（実績報告書③）"
        description={
          <span className="bs-catch-names">
            水揚仕切書の名義確認：船主 <strong>{ownerName || '—'}</strong>
            <span aria-hidden>／</span>船長（速報） <strong>{captainNames || (vesselId != null && voyageFrom && voyageTo ? '該当する速報なし' : '—')}</strong>
          </span>
        }
      >
        <FormGrid columns={4}>
          <FormField label="水揚げ日" error={errors.landingDate?.message}>
            <DateField {...register('landingDate')} />
          </FormField>
          <FormField label="調査・操業漁場（農林漁区番号）" error={errors.fishingGround?.message} hint="空欄なら保存時に明細の番号から自動設定">
            <TextField {...register('fishingGround')} placeholder="自動設定" />
          </FormField>
          <FormField label="水揚合計額（税込）" error={errors.landingTotalInclTax?.message}>
            <NumCell control={control} name="landingTotalInclTax" label="水揚合計額（税込）" />
          </FormField>
          <FormField label="出荷控除額（税込）" error={errors.shippingDeductionInclTax?.message}>
            <NumCell control={control} name="shippingDeductionInclTax" label="出荷控除額（税込）" />
          </FormField>
          <FormField label="手取金額（税抜）" error={errors.netAmountExclTax?.message} hint="空欄なら保存時に 水揚合計額−出荷控除額">
            <NumCell control={control} name="netAmountExclTax" label="手取金額（税抜）" />
          </FormField>
        </FormGrid>
      </Panel>

      <Tabs
        className="bs-catch-tabs"
        items={[
          {
            key: 'entries',
            label: '漁場情報',
            badge: entries.length,
            content: (
              <div className="bs-catch-entries">
                <SubTableEditor
                  control={control}
                  name="entries"
                  duplicable
                  addLabel="明細を追加"
                  emptyMessage="明細がありません。「明細を追加」で漁場調査日ごと・魚種ごとに登録してください。"
                  createRow={() => emptyEntry(voyageFrom || '')}
                  error={errors.entries?.root?.message ?? errors.entries?.message}
                  columns={[
                    {
                      key: 'surveyDate',
                      header: '漁場調査日',
                      required: true,
                      width: 150,
                      render: ({ index, path }) => <DateField aria-label={`漁場調査日 ${index + 1}`} invalid={!!errors.entries?.[index]?.surveyDate} title={errors.entries?.[index]?.surveyDate?.message} {...register(`${path}.surveyDate`)} />,
                    },
                    { key: 'landingPort', header: '水揚港', width: 110, render: ({ path }) => <TextField aria-label="水揚港" {...register(`${path}.landingPort`)} /> },
                    {
                      key: 'zoneNo',
                      header: '農林漁区番号',
                      width: 96,
                      render: ({ index, path }) => (
                        <div className="bs-catch-zone">
                          <NumCell control={control} name={`${path}.zoneNo`} label="農林漁区番号" />
                          {record?.entries[index]?.zoneNoCalculated != null && (
                            <span className="bs-catch-zone__calc bs-num" title="機械算出">
                              算出 {record.entries[index]!.zoneNoCalculated}
                            </span>
                          )}
                          {errors.entries?.[index]?.zoneNo && <span className="bs-catch-zone__err">{errors.entries[index]!.zoneNo!.message}</span>}
                        </div>
                      ),
                    },
                    {
                      key: 'lat',
                      header: '緯度（度・分・秒）',
                      width: 190,
                      render: ({ path, index }) => {
                        const e = errors.entries?.[index];
                        const msg = e?.latDeg?.message ?? e?.latMin?.message ?? e?.latSec?.message;
                        return (
                          <>
                            <div className="bs-catch-dms">
                              <NumCell control={control} name={`${path}.latDeg`} label="緯度（度）" />
                              <NumCell control={control} name={`${path}.latMin`} label="緯度（分）" />
                              <NumCell control={control} name={`${path}.latSec`} label="緯度（秒）" />
                            </div>
                            {msg && <span className="bs-catch-zone__err">{msg}</span>}
                          </>
                        );
                      },
                    },
                    {
                      key: 'lon',
                      header: '経度（度・分・秒）',
                      width: 200,
                      render: ({ path, index }) => {
                        const e = errors.entries?.[index];
                        const msg = e?.lonDeg?.message ?? e?.lonMin?.message ?? e?.lonSec?.message;
                        return (
                          <>
                            <div className="bs-catch-dms">
                              <NumCell control={control} name={`${path}.lonDeg`} label="経度（度）" />
                              <NumCell control={control} name={`${path}.lonMin`} label="経度（分）" />
                              <NumCell control={control} name={`${path}.lonSec`} label="経度（秒）" />
                            </div>
                            {msg && <span className="bs-catch-zone__err">{msg}</span>}
                          </>
                        );
                      },
                    },
                    { key: 'operationCount', header: '操業回数', width: 80, render: ({ path }) => <NumCell control={control} name={`${path}.operationCount`} label="操業回数" /> },
                    { key: 'weather', header: '天候', width: 80, render: ({ path }) => <TextField aria-label="天候" {...register(`${path}.weather`)} /> },
                    { key: 'windDirection', header: '風向', width: 80, render: ({ path }) => <TextField aria-label="風向" {...register(`${path}.windDirection`)} /> },
                    { key: 'windForce', header: '風力', width: 70, render: ({ path }) => <TextField aria-label="風力" {...register(`${path}.windForce`)} /> },
                    { key: 'waterTemp', header: '表面水温(℃)', width: 104, render: ({ path }) => <NumCell control={control} name={`${path}.waterTemp`} label="表面水温" decimals={1} /> },
                    { key: 'depth', header: '水深(m)', width: 90, render: ({ path }) => <NumCell control={control} name={`${path}.depth`} label="水深" decimals={1} /> },
                    { key: 'current', header: '潮流', width: 90, render: ({ path }) => <TextField aria-label="潮流" {...register(`${path}.current`)} /> },
                    { key: 'sonarReaction', header: '魚探反応(m)', width: 96, render: ({ path }) => <NumCell control={control} name={`${path}.sonarReaction`} label="魚探反応" decimals={1} /> },
                    { key: 'schoolCondition', header: '魚群の状況', width: 140, render: ({ path }) => <TextField aria-label="発見時の魚群の状況" {...register(`${path}.schoolCondition`)} /> },
                    {
                      key: 'fishSpeciesId',
                      header: '魚種（マスタ）',
                      width: 170,
                      render: ({ path }) => (
                        <Controller
                          control={control}
                          name={`${path}.fishSpeciesId`}
                          render={({ field }) => (
                            <Select
                              aria-label="魚種"
                              placeholder="マスタ外"
                              options={speciesOptions}
                              value={field.value ?? ''}
                              onChange={(e) => {
                                const v = e.target.value === '' ? null : Number(e.target.value);
                                field.onChange(v);
                                const sp = species.data?.species.find((s) => s.id === v);
                                if (sp) {
                                  setValue(`${path}.standardName`, sp.standardName);
                                  setValue(`${path}.localName`, sp.localName);
                                }
                              }}
                            />
                          )}
                        />
                      ),
                    },
                    {
                      key: 'standardName',
                      header: '標準和名',
                      width: 120,
                      render: ({ path }) => <TextField aria-label="標準和名" {...register(`${path}.standardName`, { onBlur: () => linkSpecies(path, 'standardName') })} />,
                    },
                    {
                      key: 'localName',
                      header: '地方名',
                      width: 110,
                      render: ({ path }) => <TextField aria-label="地方名" {...register(`${path}.localName`, { onBlur: () => linkSpecies(path, 'localName') })} />,
                    },
                    { key: 'count', header: '尾数', width: 80, render: ({ path }) => <NumCell control={control} name={`${path}.count`} label="尾数" /> },
                    { key: 'weight', header: '重量(kg)', width: 100, render: ({ path }) => <NumCell control={control} name={`${path}.weight`} label="重量" decimals={2} /> },
                    { key: 'note', header: '備考', width: 160, render: ({ path }) => <TextField aria-label="備考" {...register(`${path}.note`)} /> },
                  ]}
                />
                {entries.length > 0 && (
                  <p className="bs-catch-entries__sum">
                    明細 <span className="bs-num">{entries.length}</span> 行 ／ 重量合計 <span className="bs-num">{formatNumber(landingTotal, 2)}</span> kg
                  </p>
                )}
              </div>
            ),
          },
          {
            key: 'reference',
            label: '参照情報（漁獲CSV取込データ）',
            badge: reference.data?.items.length,
            content: (
              <div className="bs-catch-ref">
                <p className="bs-catch-ref__note">出港日 ≦ 調査航海期間終了 かつ 入港日 ≧ 調査航海期間開始 の取込データを表示します（出港日の無い形式は水揚げ日で判定）。</p>
                {reference.error ? (
                  <ErrorState compact error={reference.error} />
                ) : (
                  <DataTable
                    caption="参照情報"
                    columns={refColumns}
                    rows={reference.data?.items ?? []}
                    rowKey={(r) => r.id}
                    loading={reference.isFetching}
                    dense
                    pagination={{ pageSize: 30 }}
                    empty={<EmptyState compact icon={<Database />} title="参照できる取込データはありません" description="事業実施者と調査航海期間を入力すると表示されます。" />}
                  />
                )}
              </div>
            ),
          },
        ]}
      />

      <FormActions>
        {!isNew && (
          <Button variant="danger" icon={<Trash2 size={16} />} onClick={() => void onDelete()} loading={remove.isPending} style={{ marginRight: 'auto' }}>
            削除
          </Button>
        )}
        <Button onClick={() => navigate(record ? yearPath(`fishing-grounds/${record.id}`) : listPath)}>キャンセル</Button>
        <Button type="submit" variant="primary" icon={<Save size={16} />} loading={isSubmitting}>
          {isNew ? '登録する' : '保存する'}
        </Button>
      </FormActions>
    </form>
  );
}
